/**
 * Generic filter utilities for reading, validating, and transforming
 * key/value filter objects used across pages (e.g. Infrastructures, Locations).
 */

/**
 * Safely converts a value to a string. Returns null if the value is null or undefined.
 */
export const toFilterString = (value: unknown): string | null => {
	if (typeof value === 'string') {
		return value;
	}
	if (typeof value === 'number') {
		return `${value}`;
	}
	if (typeof value === 'boolean') {
		return `${value}`;
	}
	return null;
};

/**
 * Returns true if at least one value in the record is a non-empty, non-nullish string.
 */
export const hasActiveFilters = (filters: Record<string, unknown>): boolean =>
	Object.values(filters).some((v) => v !== null && v !== undefined && v !== '');

/**
 * Reads a JSON filter object from localStorage by key and normalises every
 * value to `string | null` using `toFilterString`.
 *
 * @param storageKey  The localStorage key where the filters are stored.
 * @param filterKeys  The property names to extract from the stored JSON.
 * @returns A record whose keys come from `filterKeys` and whose values are `string | null`.
 */
export const getFiltersFromStorage = <K extends string>(
	storageKey: string,
	filterKeys: K[],
): Record<K, string | null> => {
	const empty = filterKeys.reduce(
		(acc, key) => {
			acc[key] = null;
			return acc;
		},
		{} as Record<K, string | null>,
	);

	const saved = localStorage.getItem(storageKey);
	if (!saved) return empty;

	try {
		const parsed = JSON.parse(saved);
		return filterKeys.reduce(
			(acc, key) => {
				acc[key] = toFilterString(parsed[key]);
				return acc;
			},
			{} as Record<K, string | null>,
		);
	} catch (error) {
		console.error('Error parsing saved filters:', error);
		return empty;
	}
};

/**
 * Builds a `URLSearchParams` instance from a filter record,
 * only including entries whose value is truthy.
 */
export const buildFilterSearchParams = (filters: Record<string, string | null | undefined>): URLSearchParams => {
	const params = new URLSearchParams();
	Object.entries(filters).forEach(([key, value]) => {
		if (value) {
			params.set(key, value);
		}
	});
	return params;
};
